import "./style.css";

type VoucherType = "company" | "massage" | "truce";

interface CouponCounts {
  company: number;
  massage: number;
  truce: number;
}

interface CouponStatusResponse {
  ok: true;
  counts: CouponCounts;
}

interface CouponRedeemResponse extends CouponStatusResponse {
  couponCode: string;
}

interface CouponCopy {
  title: string;
  description: string;
  noteLabel: string;
  notePlaceholder: string;
  requiresOption: boolean;
}

const couponCopy: Record<VoucherType, CouponCopy> = {
  company: {
    title: "陪妳去做想做的事券",
    description: "不抱怨、不拒絕，認真陪伴，也認真給建議。",
    noteLabel: "這次想一起做什麼？（選填）",
    notePlaceholder: "例如：陪我去逛一整天、一起學一件新事…",
    requiresOption: false,
  },
  massage: {
    title: "20 分鐘認真按摩券",
    description: "肩頸、背部或腿部任選；完整 20 分鐘，絕不偷懶。",
    noteLabel: "還有什麼指定事項？（選填）",
    notePlaceholder: "例如：右邊肩膀要多按一點…",
    requiresOption: true,
  },
  truce: {
    title: "先抱抱，再好好說券",
    description: "不管當下多生氣，我們先停止爭吵；冷靜後，再把事情好好說完。",
    noteLabel: "有什麼想先讓我知道的？（選填）",
    notePlaceholder: "可以先留一句話給我…",
    requiresOption: false,
  },
};

let activeVoucherType: VoucherType | null = null;

/**
 * 取得必要的 DOM 元素，適合初始化時使用。
 *
 * @param selector CSS selector，必須只對應到一個必要元素。
 * @returns 指定型別的 DOM 元素。
 * @throws 找不到元素時拋出錯誤，避免互動功能靜默失效。
 */
function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`頁面缺少必要元素：${selector}`);
  }
  return element;
}

/**
 * 判斷未知資料是否為三種使用券的剩餘張數。
 *
 * @param value API 回傳的未知資料。
 * @returns 通過欄位、整數與 0–100 範圍檢查時為 true。
 */
function isCouponCounts(value: unknown): value is CouponCounts {
  if (!value || typeof value !== "object") {
    return false;
  }

  const record = value as Record<string, unknown>;
  return (["company", "massage", "truce"] as const).every((key) => {
    const count = record[key];
    return typeof count === "number" && Number.isInteger(count) && count >= 0 && count <= 100;
  });
}

/**
 * 判斷狀態 API 是否回傳可用的使用券資料。
 *
 * @param value API 回傳的未知 JSON。
 * @returns 格式為成功狀態且包含有效 counts 時為 true。
 */
function isCouponStatusResponse(value: unknown): value is CouponStatusResponse {
  if (!value || typeof value !== "object") {
    return false;
  }
  const record = value as Record<string, unknown>;
  return record.ok === true && isCouponCounts(record.counts);
}

/**
 * 判斷兌券 API 是否回傳完整的券號與剩餘張數。
 *
 * @param value API 回傳的未知 JSON。
 * @returns 格式完整且券號非空白時為 true。
 */
function isCouponRedeemResponse(value: unknown): value is CouponRedeemResponse {
  if (!value || typeof value !== "object") {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    record.ok === true &&
    isCouponCounts(record.counts) &&
    typeof record.couponCode === "string" &&
    record.couponCode.trim().length > 0
  );
}

/**
 * 將 API 的剩餘張數同步到三張票券，並依庫存控制按鈕狀態。
 *
 * @param counts 三種使用券目前的剩餘張數。
 * @returns 無回傳值。
 */
function renderCouponCounts(counts: CouponCounts): void {
  (Object.entries(counts) as Array<[VoucherType, number]>).forEach(([type, count]) => {
    document.querySelectorAll<HTMLElement>(`[data-count="${type}"]`).forEach((element) => {
      element.textContent = String(count);
    });
    document.querySelectorAll<HTMLButtonElement>(`[data-redeem="${type}"]`).forEach((button) => {
      button.disabled = count <= 0;
      button.textContent = count <= 0 ? "已全部使用" : "使用一張";
    });
  });
}

/**
 * 更新 Google Sheet 同步狀態的提示樣式與文字。
 *
 * @param state loading、ready 或 error。
 * @param message 顯示給使用者的簡短狀態說明。
 * @returns 無回傳值。
 */
function setSyncStatus(state: "loading" | "ready" | "error", message: string): void {
  const status = requireElement<HTMLElement>("#coupon-sync-status");
  status.classList.toggle("is-ready", state === "ready");
  status.classList.toggle("is-error", state === "error");
  const text = status.querySelector<HTMLSpanElement>("span:last-child");
  if (text) {
    text.textContent = message;
  }
}

/**
 * 控制所有兌券按鈕是否允許操作。
 *
 * @param enabled API 與 Google Sheet 已可用時傳入 true。
 * @returns 無回傳值。
 */
function setCouponButtonsEnabled(enabled: boolean): void {
  document.querySelectorAll<HTMLButtonElement>("[data-redeem]").forEach((button) => {
    button.disabled = !enabled;
  });
}

/**
 * 從同源 Pages Function 讀取最新券數；失敗時停用兌券以避免假成功。
 *
 * @returns 完成同步後結束，不直接回傳資料。
 */
async function loadCouponCounts(): Promise<void> {
  setSyncStatus("loading", "正在確認剩餘張數…");
  setCouponButtonsEnabled(false);

  try {
    const response = await fetch("/api/coupons", {
      headers: { Accept: "application/json" },
    });
    const payload: unknown = await response.json();
    if (!response.ok || !isCouponStatusResponse(payload)) {
      throw new Error("使用券服務尚未準備完成");
    }

    setCouponButtonsEnabled(true);
    renderCouponCounts(payload.counts);
    setSyncStatus("ready", "已和專屬使用清單同步");
  } catch (error) {
    const reason = error instanceof Error ? error.message : "無法確認使用券狀態";
    console.info(`使用券尚未同步：${reason}`);
    setSyncStatus("error", "使用券暫時無法同步，請稍後再試");
    setCouponButtonsEnabled(false);
  }
}

/**
 * 建立散落彩帶並在動畫結束後移除，僅作視覺慶祝用途。
 *
 * @param amount 彩帶數量；手機上建議控制在 100 以下。
 * @returns 無回傳值。
 */
function burstConfetti(amount = 76): void {
  const layer = requireElement<HTMLElement>("#confetti-layer");
  const colors = ["#ef6544", "#ffd66b", "#74bce9", "#ffffff", "#ff9f8a"];

  for (let index = 0; index < amount; index += 1) {
    const piece = document.createElement("span");
    piece.className = "confetti-piece";
    piece.style.left = `${Math.random() * 100}%`;
    piece.style.backgroundColor = colors[index % colors.length] ?? "#ef6544";
    piece.style.setProperty("--fall-duration", `${2.1 + Math.random() * 1.8}s`);
    piece.style.setProperty("--drift", `${-90 + Math.random() * 180}px`);
    piece.style.setProperty("--spin", `${360 + Math.random() * 900}deg`);
    piece.style.animationDelay = `${Math.random() * 0.35}s`;
    layer.append(piece);
    window.setTimeout(() => piece.remove(), 4400);
  }
}

/**
 * 設定捲動進度、章節導覽與進場動畫。
 *
 * @returns 無回傳值。
 */
function initializeScrollExperience(): void {
  const progressBar = requireElement<HTMLElement>("#scroll-progress-bar");
  const navLinks = [...document.querySelectorAll<HTMLAnchorElement>(".chapter-dot")];
  const sections = ["top", "story", "letter", "coupons"]
    .map((id) => document.getElementById(id))
    .filter((section): section is HTMLElement => section !== null);

  const updateProgress = (): void => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const progress = scrollable > 0 ? Math.min(window.scrollY / scrollable, 1) : 0;
    progressBar.style.transform = `scaleX(${progress})`;
  };

  window.addEventListener("scroll", updateProgress, { passive: true });
  updateProgress();

  const chapterObserver = new IntersectionObserver(
    (entries) => {
      const visibleEntry = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visibleEntry) {
        return;
      }
      navLinks.forEach((link) => {
        link.classList.toggle("is-active", link.getAttribute("href") === `#${visibleEntry.target.id}`);
      });
    },
    { rootMargin: "-30% 0px -55%", threshold: [0, 0.2, 0.5] },
  );
  sections.forEach((section) => chapterObserver.observe(section));

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const revealItems = document.querySelectorAll<HTMLElement>(".reveal-item");
  if (prefersReducedMotion) {
    revealItems.forEach((item) => item.classList.add("is-visible"));
    return;
  }

  const revealObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: "0px 0px -8%" },
  );
  revealItems.forEach((item) => revealObserver.observe(item));
}

/**
 * 開啟指定類型的使用券確認視窗，並依券種切換表單欄位。
 *
 * @param type company、massage 或 truce。
 * @returns 無回傳值。
 */
function openCouponDialog(type: VoucherType): void {
  activeVoucherType = type;
  const copy = couponCopy[type];
  const dialog = requireElement<HTMLDialogElement>("#coupon-dialog");
  const form = requireElement<HTMLFormElement>("#coupon-form");
  const success = requireElement<HTMLElement>("#redeem-success");
  const optionField = requireElement<HTMLElement>("#option-field");
  const note = requireElement<HTMLTextAreaElement>("#coupon-note");

  form.hidden = false;
  success.hidden = true;
  optionField.hidden = !copy.requiresOption;
  requireElement<HTMLElement>("#dialog-title").textContent = copy.title;
  requireElement<HTMLElement>("#dialog-description").textContent = copy.description;
  requireElement<HTMLElement>("#note-label").textContent = copy.noteLabel;
  note.placeholder = copy.notePlaceholder;
  note.value = "";
  requireElement<HTMLElement>("#dialog-error").hidden = true;
  document.body.classList.add("dialog-open");
  dialog.showModal();
}

/**
 * 關閉兌券視窗並重設目前券種。
 *
 * @returns 無回傳值。
 */
function closeCouponDialog(): void {
  const dialog = requireElement<HTMLDialogElement>("#coupon-dialog");
  if (dialog.open) {
    dialog.close();
  }
  activeVoucherType = null;
  document.body.classList.remove("dialog-open");
}

/**
 * 將一張使用券送到 Pages Function；只有後端確認寫入 Sheet 後才顯示成功。
 *
 * @param event 表單 submit 事件。
 * @returns 兌券請求處理完成後結束。
 */
async function redeemCoupon(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  if (!activeVoucherType) {
    return;
  }

  const submitButton = requireElement<HTMLButtonElement>("#coupon-submit");
  const errorBox = requireElement<HTMLElement>("#dialog-error");
  const note = requireElement<HTMLTextAreaElement>("#coupon-note").value.trim();
  const option =
    activeVoucherType === "massage"
      ? requireElement<HTMLSelectElement>("#coupon-option").value
      : "";

  submitButton.disabled = true;
  submitButton.querySelector("span")!.textContent = "正在記錄這張約定…";
  errorBox.hidden = true;

  try {
    const response = await fetch("/api/coupons", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        voucherType: activeVoucherType,
        option,
        note,
        requestId: crypto.randomUUID(),
      }),
    });
    const payload: unknown = await response.json();
    if (!response.ok || !isCouponRedeemResponse(payload)) {
      const possibleError = payload as { error?: unknown };
      throw new Error(
        typeof possibleError.error === "string" ? possibleError.error : "使用券沒有成功寫入，請再試一次。",
      );
    }

    renderCouponCounts(payload.counts);
    requireElement<HTMLElement>("#success-code").textContent = payload.couponCode;
    requireElement<HTMLFormElement>("#coupon-form").hidden = true;
    requireElement<HTMLElement>("#redeem-success").hidden = false;
    burstConfetti(48);
  } catch (error) {
    errorBox.textContent =
      error instanceof Error ? error.message : "目前無法記錄這張券，請檢查網路後再試一次。";
    errorBox.hidden = false;
  } finally {
    submitButton.disabled = false;
    submitButton.querySelector("span")!.textContent = "確定使用一張";
  }
}

/**
 * 綁定開場、吹蠟燭、兌券視窗與鍵盤相關互動。
 *
 * @returns 無回傳值。
 */
function initializeInteractions(): void {
  const hero = requireElement<HTMLElement>(".hero");
  window.requestAnimationFrame(() => hero.classList.add("is-ready"));

  requireElement<HTMLButtonElement>("#open-story").addEventListener("click", () => {
    requireElement<HTMLElement>(".opening-note").scrollIntoView({ behavior: "smooth" });
    burstConfetti(34);
  });

  requireElement<HTMLButtonElement>("#make-wish").addEventListener("click", (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    const section = requireElement<HTMLElement>(".wish-section");
    if (section.classList.contains("wish-made")) {
      return;
    }
    section.classList.add("wish-made");
    button.textContent = "願望已經收好了";
    button.disabled = true;
    burstConfetti(96);
  });

  document.querySelectorAll<HTMLButtonElement>("[data-redeem]").forEach((button) => {
    button.addEventListener("click", () => {
      const type = button.dataset.redeem;
      if (type === "company" || type === "massage" || type === "truce") {
        openCouponDialog(type);
      }
    });
  });

  requireElement<HTMLButtonElement>("#dialog-close").addEventListener("click", closeCouponDialog);
  requireElement<HTMLButtonElement>("#success-close").addEventListener("click", closeCouponDialog);
  requireElement<HTMLFormElement>("#coupon-form").addEventListener("submit", (event) => {
    void redeemCoupon(event);
  });

  const dialog = requireElement<HTMLDialogElement>("#coupon-dialog");
  dialog.addEventListener("cancel", () => {
    activeVoucherType = null;
    document.body.classList.remove("dialog-open");
  });
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) {
      closeCouponDialog();
    }
  });
}

/**
 * 啟動整份生日網站；任何初始化錯誤都會輸出具體訊息供部署後排查。
 *
 * @returns 初始券數同步完成後結束。
 */
async function initialize(): Promise<void> {
  initializeScrollExperience();
  initializeInteractions();
  await loadCouponCounts();
}

void initialize().catch((error: unknown) => {
  const message = error instanceof Error ? error.message : "網站初始化失敗";
  console.error(message);
  setSyncStatus("error", "使用券功能暫時無法啟動，請稍後重新整理");
});
